import { useReducer, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation } from 'convex/react';
import { api } from '../../convex/_generated/api';
import { PostWriterSubmenuMemo } from '@/components/post-writer/PostWriterSubmenu';
import { Button } from '@/components/ui/button';
import { Loader2, Plus, Upload } from 'lucide-react';
import type { Id } from '../../convex/_generated/dataModel';
import { SaveAvatarModal } from '@/components/ai-avatar/SaveAvatarModal';
import { AVATAR_UPLOAD_CONSTANTS } from '@/@types/avatars';
import { AvatarFolderGrid } from '@/pages/AvatarFolderGrid';
import {
  CreateFolderDialog,
  EditFolderDialog,
  DeleteFolderDialog,
} from '@/pages/AvatarFolderDialogs';
import { useAvatarUpload } from '@/hooks/useAvatarUpload';

interface IMyAvatarsState {
  isCreateDialogOpen: boolean;
  editingFolder: { id: Id<'avatarFolders'>; title: string } | null;
  newFolderTitle: string;
  newFolderDescription: string;
  isSubmitting: boolean;
  deletingFolderId: Id<'avatarFolders'> | null;
}

type MyAvatarsAction =
  | { type: 'OPEN_CREATE' }
  | { type: 'CLOSE_CREATE' }
  | { type: 'SET_EDITING_FOLDER'; folder: IMyAvatarsState['editingFolder'] }
  | { type: 'SET_TITLE'; title: string }
  | { type: 'SET_DESCRIPTION'; description: string }
  | { type: 'SET_SUBMITTING'; value: boolean }
  | { type: 'SET_DELETING'; folderId: Id<'avatarFolders'> | null }
  | { type: 'RESET_CREATE' };

function myAvatarsReducer(state: IMyAvatarsState, action: MyAvatarsAction): IMyAvatarsState {
  switch (action.type) {
    case 'OPEN_CREATE':
      return { ...state, isCreateDialogOpen: true };
    case 'CLOSE_CREATE':
      return { ...state, isCreateDialogOpen: false, newFolderTitle: '', newFolderDescription: '' };
    case 'SET_EDITING_FOLDER':
      return { ...state, editingFolder: action.folder };
    case 'SET_TITLE':
      return { ...state, newFolderTitle: action.title };
    case 'SET_DESCRIPTION':
      return { ...state, newFolderDescription: action.description };
    case 'SET_SUBMITTING':
      return { ...state, isSubmitting: action.value };
    case 'SET_DELETING':
      return { ...state, deletingFolderId: action.folderId };
    case 'RESET_CREATE':
      return {
        ...state,
        isCreateDialogOpen: false,
        newFolderTitle: '',
        newFolderDescription: '',
        isSubmitting: false,
      };
  }
}

const INITIAL_STATE: IMyAvatarsState = {
  isCreateDialogOpen: false,
  editingFolder: null,
  newFolderTitle: '',
  newFolderDescription: '',
  isSubmitting: false,
  deletingFolderId: null,
};

export const MyAvatarsPage = () => {
  const navigate = useNavigate();
  const folders = useQuery(api.avatarFolders.getActiveFolders);
  const createFolder = useMutation(api.avatarFolders.createFolder);
  const updateFolder = useMutation(api.avatarFolders.updateFolder);
  const deleteFolder = useMutation(api.avatarFolders.deleteFolder);

  const [state, dispatch] = useReducer(myAvatarsReducer, INITIAL_STATE);

  const upload = useAvatarUpload(createFolder);

  const handleCreateFolder = useCallback(async () => {
    if (!state.newFolderTitle.trim()) return;
    dispatch({ type: 'SET_SUBMITTING', value: true });
    try {
      await createFolder({
        title: state.newFolderTitle.trim(),
        description: state.newFolderDescription.trim() || undefined,
      });
      dispatch({ type: 'RESET_CREATE' });
    } finally {
      dispatch({ type: 'SET_SUBMITTING', value: false });
    }
  }, [createFolder, state.newFolderTitle, state.newFolderDescription]);

  const handleUpdateFolder = useCallback(async () => {
    if (!state.editingFolder || !state.editingFolder.title.trim()) return;
    dispatch({ type: 'SET_SUBMITTING', value: true });
    try {
      await updateFolder({
        folderId: state.editingFolder.id,
        title: state.editingFolder.title.trim(),
      });
      dispatch({ type: 'SET_EDITING_FOLDER', folder: null });
    } finally {
      dispatch({ type: 'SET_SUBMITTING', value: false });
    }
  }, [updateFolder, state.editingFolder]);

  const handleDeleteFolder = useCallback(async () => {
    if (!state.deletingFolderId) return;
    await deleteFolder({ folderId: state.deletingFolderId });
    dispatch({ type: 'SET_DELETING', folderId: null });
  }, [deleteFolder, state.deletingFolderId]);

  return {
    submenu: <PostWriterSubmenuMemo />,
    mainContent: (
      <div className="h-full bg-background">
        <div className="p-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-2xl font-bold text-foreground">My Avatars</h1>
              <p className="text-sm text-muted-foreground mt-1">
                Generate and organize AI avatar images with character consistency.
              </p>
            </div>
            <div className="flex items-center gap-2">
              <label htmlFor="my-avatars-file-upload" aria-label="Upload avatar image">
                <input
                  id="my-avatars-file-upload"
                  type="file"
                  accept={AVATAR_UPLOAD_CONSTANTS.ACCEPTED_EXTENSIONS}
                  onChange={upload.handleFileSelect}
                  className="hidden"
                  disabled={upload.isFileUploading}
                />
                <Button variant="outline" size="sm" disabled={upload.isFileUploading} asChild>
                  <span>
                    {upload.isFileUploading ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <Upload className="h-4 w-4 mr-2" />
                    )}
                    {upload.isFileUploading ? 'Uploading...' : 'Upload'}
                  </span>
                </Button>
              </label>
              <Button onClick={() => navigate('/ai-avatar/my-avatars/generate')} size="sm">
                <Plus className="h-4 w-4 mr-2" />
                Generate New
              </Button>
            </div>
          </div>

          <AvatarFolderGrid
            folders={folders}
            isLoading={folders === undefined}
            onEditFolder={(id, title) =>
              dispatch({ type: 'SET_EDITING_FOLDER', folder: { id, title } })
            }
            onDeleteFolder={folderId => dispatch({ type: 'SET_DELETING', folderId })}
          />

          <CreateFolderDialog
            open={state.isCreateDialogOpen}
            onOpenChange={open =>
              open ? dispatch({ type: 'OPEN_CREATE' }) : dispatch({ type: 'CLOSE_CREATE' })
            }
            title={state.newFolderTitle}
            onTitleChange={title => dispatch({ type: 'SET_TITLE', title })}
            description={state.newFolderDescription}
            onDescriptionChange={description => dispatch({ type: 'SET_DESCRIPTION', description })}
            onSubmit={handleCreateFolder}
            isSubmitting={state.isSubmitting}
          />

          <EditFolderDialog
            editingFolder={state.editingFolder}
            onEditingFolderChange={folder => dispatch({ type: 'SET_EDITING_FOLDER', folder })}
            onSubmit={handleUpdateFolder}
            isSubmitting={state.isSubmitting}
          />

          <SaveAvatarModal
            isOpen={upload.uploadModalOpen}
            onClose={upload.handleUploadClose}
            onSave={upload.handleUploadSave}
            isSaving={upload.isUploading}
            selectedImages={upload.uploadedImage ? [upload.uploadedImage] : []}
            folders={folders ?? []}
            selectedFolderId={upload.uploadFolderId}
            onFolderChange={upload.setUploadFolderId}
            newFolderTitle={upload.uploadFolderTitle}
            onNewFolderTitleChange={upload.setUploadFolderTitle}
          />

          <DeleteFolderDialog
            deletingFolderId={state.deletingFolderId}
            onOpenChange={folderId => dispatch({ type: 'SET_DELETING', folderId })}
            onConfirm={handleDeleteFolder}
          />
        </div>
      </div>
    ),
  };
};
